<template>
    <div
        :class="[{ 'alt-styles': props.blankState }, { 'highlight-icon': highlightIcon }]"
        class="moat-date-range-picker"
    >
        <VueDatePicker
            ref="datePickerRef"
            v-model="date"
            class="date-range-hook"
            :range="props.type === 'dateRange'"
            :multi-calendars="props.type === 'dateRange'"
            :enable-time-picker="false"
            :preset-dates="!props.blankState ? presetDates : []"
            :min-date="props.blankState ? undefined : today"
            :clearable="false"
            position="center"
            :teleport="true"
            auto-position="bottom"
            :config="{ arrowLeft: '50%' }"
            @update:model-value="handleDateChange"
        >
            <template #preset-date-range-button="{ label, value, presetDate }">
                <div class="preset-label">
                    <span
                        :tabindex="0"
                        @click="presetDate(value)"
                        @keyup.enter.prevent="presetDate(value)"
                        @keyup.space.prevent="presetDate(value)"
                    >
                        {{ label }}
                    </span>
                </div>
            </template>
        </VueDatePicker>
    </div>
</template>
<script setup>
import { inject, ref, onMounted, defineExpose } from 'vue'
import { usePortfolioManagerStore } from '@/stores'

import {
    endOfMonth,
    endOfYear,
    startOfMonth,
    startOfYear,
    startOfQuarter,
    endOfQuarter,
    addQuarters,
    addYears,
    differenceInDays,
} from 'date-fns'

const portfolioManagerStore = usePortfolioManagerStore()
const eventBus = inject('eventBus')
const emit = defineEmits(['update:modelValue'])

const props = defineProps({
    type: {
        type: String,
        default: 'dateRange',
    },
    blankState: {
        type: Boolean,
        default: false,
    },
})

const date = ref()
const datePickerRef = ref(null)
const previousDate = ref()
const today = ref(new Date())
const highlightIcon = ref(false)

const presetDates = ref([
    { label: 'This month', value: [today.value, endOfMonth(new Date())] },
    { label: 'This quarter', value: [today.value, endOfQuarter(new Date())] },
    {
        label: 'Next quarter',
        value: [
            startOfQuarter(addQuarters(new Date(), 1)),
            endOfQuarter(addQuarters(new Date(), 1)),
        ],
    },
    { label: 'This year', value: [today.value, endOfYear(new Date())] },
    {
        label: 'Next year',
        value: [startOfYear(addYears(new Date(), 1)), endOfYear(addYears(new Date(), 1))],
    },
])

onMounted(() => {
    if (props.blankState) {
        return
    }

    if (props.type === 'dateRange') {
        const startDate = new Date()
        const endDate = endOfYear(new Date())
        date.value = [startDate, endDate]
        previousDate.value = [...date.value]
    } else {
        const singleDate = new Date()
        date.value = singleDate
        previousDate.value = singleDate
    }
})

const handleDateChange = (value) => {
    if (props.type === 'dateRange') {
        // Check if both start and end dates are selected
        if (!value || !Array.isArray(value) || value.length < 2 || !value[0] || !value[1]) {
            date.value = [...previousDate.value]
            return
        }

        const daysDifference = differenceInDays(new Date(value[1]), new Date(value[0]))
        if (daysDifference < 1) {
            date.value = [...previousDate.value]
            eventBus.emit('snacktime', {
                type: 'caution',
                message: 'Please select a date range of at least 1 full day.',
                contextual: false,
            })
            return
        }

        const formatDate = (date) => {
            const newDate = new Date(date)
            newDate.setHours(0, 0, 0, 0)
            return newDate.toISOString().split('T')[0]
        }

        previousDate.value = [...value]

        portfolioManagerStore.dateRange = [formatDate(value[0]), formatDate(value[1])]
        emit('update:modelValue', portfolioManagerStore.dateRange)

        if (props.blankState && (value[0] || value[1])) {
            highlightIcon.value = true
        }
    } else {
        // Single date picker scenario
        if (!value) {
            date.value = previousDate.value
            return
        }

        const formatDate = (d) => {
            const newDate = new Date(d)
            newDate.setHours(0, 0, 0, 0)
            return newDate.toISOString().split('T')[0]
        }

        previousDate.value = value

        const formattedDate = formatDate(value)
        emit('update:modelValue', formattedDate)

        if (props.blankState && value) {
            highlightIcon.value = true
        }
    }
}

// Expose methods to parent components
defineExpose({
    openMenu() {
        datePickerRef.value?.openMenu?.()
    },
})
</script>

<style lang="scss" scoped>
.moat-date-range-picker {
    width: 425px;
    height: 100%;

    &.alt-styles {
        width: 250px;
        height: 40px;
        border-radius: 0 !important;
        background: white !important;

        .date-range-hook {
            border: none;
            border-radius: 0 !important;
            border-bottom: 1px solid var(--color-stone-400);
            border-top: 1px solid var(--color-stone-400);
            padding: 9px 10px 8px 0 !important;
            height: 100%;

            :deep(.dp__input) {
                font-size: 15px;
                height: 100% !important;
                padding: 0 !important;
                margin: 0 !important;
                border: none !important;
                border-radius: 0 !important;
                border: none !important;
                padding-left: 38px !important;
            }

            :deep(.dp__input_icon) {
                width: 18px !important;
                height: 18px !important;
            }
        }

        &.highlight-icon {
            :deep(.dp__input_icon) {
                color: var(--color-moat-600) !important;
            }
        }
    }

    .date-range-hook {
        border-radius: 8px;
        border: 3px solid var(--color-moat-600);

        :deep(.dp__theme_light) {
            --dp-arrow-left: 50% !important;
        }

        :deep(.dp--preset-range) {
            padding: 10px;
            margin: 2px 0;
            text-align: center;

            transition: all 0.3s ease-in-out;

            &:hover {
                background: rgba(var(--color-moat-600), 0.2) !important;
            }
        }

        :deep(.dp__action_button) {
            height: 40px;
            font-size: 16px;
            margin: 0 4px;
            padding: 4px 16px;
        }

        :deep(.dp--menu-wrapper) {
            box-shadow: 0 0 30px rgba(0, 0, 0, 0.2);
        }
    }

    .preset-label {
    }
}
</style>
